import { Roles, RolesDescriptor } from '@/auth/azure/Roles'
import { formatDate } from 'devextreme/localization'
import { user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  ExtendedAppointment,
  CalendarRoom,
  FlatCalendarNode,
  CustomGroupsConfig,
  CustomGroup,
  FormLocation,
  FormAppointment,
  CalendarFormDataInput,
} from '@/routes/calendar/CalendarPage.types'
import { isUserRoleAllowed } from '@/routes/utils'

export const buildCalendarFormData = ({
  values,
  newAttachments,
  removedAttachmentIds,
}: CalendarFormDataInput): FormData => {
  const fd = new FormData()

  const appendIfDefined = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== '') {
      fd.append(key, String(value))
    }
  }

  // Scalar fields
  appendIfDefined('Text', values.text)
  appendIfDefined('Description', values.description)
  appendIfDefined('Status', values.status)
  appendIfDefined('AppointmentId', values.appointmentId)
  appendIfDefined('AllDay', values.allDay)
  appendIfDefined('IsOnlineMeeting', values.isOnlineMeeting)
  appendIfDefined('IsReminderOn', values.isReminderOn)
  appendIfDefined('ReminderMinutesBeforeStart', values.reminderMinutesBeforeStart)
  appendIfDefined('Email', values.email)
  appendIfDefined('RecurrenceRule', values.recurrenceRule)
  appendIfDefined('TaskId', values.taskId == null ? '' : String(values.taskId))

  // Dates as ISO strings
  if (values.startDate)
    fd.append('StartDate', values.startDate instanceof Date ? values.startDate.toISOString() : String(values.startDate))
  if (values.endDate)
    fd.append('EndDate', values.endDate instanceof Date ? values.endDate.toISOString() : String(values.endDate))

  // User (nested)
  if (values.user) {
    appendIfDefined('User.Email', values.user.email)
    appendIfDefined('User.FullName', values.user.fullname)
  }

  // Location (nested)
  if (values.location) {
    appendIfDefined('Location.DisplayName', values.location.displayName ?? values.location.fullname)
    appendIfDefined('Location.Email', values.location.email)
    appendIfDefined('Location.LocationUri', values.location.locationUri ?? values.location.email)
  }

  // Attendees (indexed bracket notation for ASP.NET model binding)
  ;(values.requiredAttendees as { email: string; fullname?: string }[] | undefined)?.forEach((att, i) => {
    fd.append(`RequiredAttendees[${i}].Email`, att.email)
    if (att.fullname) fd.append(`RequiredAttendees[${i}].FullName`, att.fullname)
  })
  ;(values.optionalAttendees as { email: string; fullname?: string }[] | undefined)?.forEach((att, i) => {
    fd.append(`OptionalAttendees[${i}].Email`, att.email)
    if (att.fullname) fd.append(`OptionalAttendees[${i}].FullName`, att.fullname)
  })

  // File attachments
  newAttachments?.forEach((file) => {
    fd.append('newAttachments', file)
  })

  // Removed attachment IDs
  removedAttachmentIds?.forEach((id) => {
    fd.append('removedAttachmentIds', id)
  })

  return fd
}

export const toDateOnly = (d: Date): Date => {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
}

export const toTimeOnly = (d: Date): Date => {
  return new Date(1970, 0, 1, d.getHours(), d.getMinutes(), 0, 0)
}

export const combineDateAndTime = (date: Date, time: Date): Date => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    0,
  )
}

export const normalizeAppointmentDateToSlot = (date: Date, slotMinutes = 30): Date => {
  if (Number.isNaN(date.getTime())) return date

  const slotMs = slotMinutes * 60 * 1000
  const normalized = new Date(Math.round(date.getTime() / slotMs) * slotMs)
  normalized.setSeconds(0, 0)
  return normalized
}

const formatAppointmentRange = (startDate: Date, endDate: Date, allDay?: boolean): string => {
  const isSameDay = toDateOnly(startDate).getTime() === toDateOnly(endDate).getTime()

  if (allDay) {
    return isSameDay
      ? `${formatDate(startDate, 'shortDate')} (tutto il giorno)`
      : `${formatDate(startDate, 'shortDate')} - ${formatDate(endDate, 'shortDate')} (tutto il giorno)`
  }

  return isSameDay
    ? `${formatDate(startDate, 'shortDate')} ${formatDate(startDate, 'shortTime')} - ${formatDate(endDate, 'shortTime')}`
    : `${formatDate(startDate, 'shortDateShortTime')} - ${formatDate(endDate, 'shortDateShortTime')}`
}

// Messaggio di conferma per lo spostamento (trascinamento) di un appuntamento sul calendario.
// Ridimensionamento (resize) e duplicazione (ctrl + trascinamento) non richiedono conferma.
// Restituisce null quando date e durata non cambiano: in quel caso non c'è nulla da confermare.
export const buildAppointmentMoveConfirm = (
  oldData: Partial<ExtendedAppointment> | undefined,
  newData: Partial<ExtendedAppointment> | undefined,
): { title: string; messageHtml: string } | null => {
  const oldStartDate = new Date(oldData?.startDate ?? '')
  const oldEndDate = new Date(oldData?.endDate ?? '')
  const newStartDate = new Date(newData?.startDate ?? '')
  const newEndDate = new Date(newData?.endDate ?? '')
  if ([oldStartDate, oldEndDate, newStartDate, newEndDate].some((date) => Number.isNaN(date.getTime()))) return null

  const oldAllDay = oldData?.allDay ?? false
  const newAllDay = newData?.allDay ?? false
  if (
    oldStartDate.getTime() === newStartDate.getTime() &&
    oldEndDate.getTime() === newEndDate.getTime() &&
    oldAllDay === newAllDay
  ) {
    return null
  }

  const appointmentText = newData?.text ?? oldData?.text ?? 'senza oggetto'

  return {
    title: 'Spostamento evento',
    messageHtml:
      `<i>Vuoi spostare l'evento <b>${appointmentText}</b>?</i>` +
      `<br/><br/>Da: <b>${formatAppointmentRange(oldStartDate, oldEndDate, oldAllDay)}</b>` +
      `<br/>A: <b>${formatAppointmentRange(newStartDate, newEndDate, newAllDay)}</b>`,
  }
}

export const mapAppointmentLocation = (loc: any): FormLocation | undefined => {
  return loc?.displayName ? { userid: loc.uniqueId, fullname: loc.displayName, email: loc.locationUri } : undefined
}

export const canManageAppointment = (
  userRoles: RolesDescriptor | undefined,
  userEmail: string | null | undefined,
  appointmentEmail: string | null | undefined,
  isOrganizer?: boolean,
): boolean => {
  // Evento ricevuto come invito: la copia sul calendario non è modificabile da nessuno, va gestita
  // dall'organizzatore. Il flag arriva da Graph (isOrganizer); se manca si assume evento proprio.
  if (isOrganizer === false) return false

  return (
    isUserRoleAllowed(userRoles, [Roles.Supervisor, Roles.Administrator, Roles.GlobalAdministrator, Roles.Organizer]) ||
    appointmentEmail === userEmail
  )
}

export const buildEditData = (
  appt: Partial<ExtendedAppointment>,
  overrides?: Partial<ExtendedAppointment>,
): FormAppointment => {
  const startDate = new Date(appt.startDate ?? '')
  const endDate = new Date(appt.endDate ?? '')
  const normalizedTaskId =
    appt.taskId === undefined || appt.taskId === null || appt.taskId === '' ? undefined : Number(appt.taskId)
  return {
    ...appt,
    'start/date': toDateOnly(startDate),
    'start/time': toTimeOnly(startDate),
    'end/date': toDateOnly(endDate),
    'end/time': toTimeOnly(endDate),
    allDay: appt.allDay ?? false,
    isReminderOn: appt.isReminderOn ?? false,
    reminderMinutesBeforeStart: appt.reminderMinutesBeforeStart ?? 0,
    isOnlineMeeting: appt.isOnlineMeeting ?? false,
    location: mapAppointmentLocation(appt.location),
    status: appt.status ?? 'Busy',
    taskId: Number.isFinite(normalizedTaskId) ? normalizedTaskId : undefined,
    ...overrides,
  }
}

export const removeById = (items: ExtendedAppointment[], id: string | undefined): ExtendedAppointment[] => {
  return items.filter((item) => item.appointmentId !== id)
}

export const generateColorByUserId = (userid: number): string => {
  // Use a combination of a prime number and a multiplier for better distribution
  const hue = (userid * 43 + 17) % 360 // 43 and 17 are primes for better distribution
  const saturation = 40 + (Math.abs(userid * 3) % 40) // Saturation between 40% and 80%
  const lightness = 30 + (Math.abs(userid * 5) % 30) // Lightness between 30% and 60%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

// Converte email strings → oggetti { email, fullname } per il backend
export const expandAttendeesToPayload = (
  emails: string[] | undefined,
  users: ((user | CalendarRoom) & {
    color?: string
  })[],
) =>
  (emails ?? []).map((email) => {
    const found = users.find((u) => u.email === email)
    return { email, fullname: found?.fullname ?? email }
  })

// Converte user[] | string[] → string[] di email (usato in loadWithPending dopo form save)
export const toEmailStrings = (attendees: unknown[] | undefined): string[] =>
  (attendees ?? []).map((a) => (typeof a === 'string' ? a : ((a as user).email ?? ''))).filter(Boolean)

export const CUSTOM_UNASSIGNED_ID = 'custom_unassigned'

export const normalizeCustomGroupsConfig = (
  config: Partial<CustomGroupsConfig> | null | undefined,
): CustomGroupsConfig => {
  const rawGroups = (Array.isArray(config?.groups) ? config.groups : []) as Array<
    Partial<CustomGroup> | null | undefined
  >
  const isGroupLike = (
    group: Partial<CustomGroup> | null | undefined,
  ): group is { id: string; name: string; parentId?: string | null } => !!group?.id && !!group?.name
  const candidateGroups: CustomGroup[] = rawGroups.filter(isGroupLike).map((group) => ({
    id: group.id,
    name: group.name,
    parentId: group.parentId ?? null,
  }))

  const groupMap = new Map(candidateGroups.map((group) => [group.id, group]))
  const hasValidParentChain = (group: CustomGroup) => {
    const visited = new Set<string>([group.id])
    let currentParentId = group.parentId
    while (currentParentId !== null) {
      if (visited.has(currentParentId)) return false
      visited.add(currentParentId)
      const parent = groupMap.get(currentParentId)
      if (!parent) return false
      currentParentId = parent.parentId
    }
    return true
  }

  const groups = candidateGroups.filter(hasValidParentChain)

  const validContainerIds = new Set(groups.map((group) => group.id))
  const assignments = Object.fromEntries(
    Object.entries(config?.assignments ?? {}).filter(
      ([, groupId]) => groupId === CUSTOM_UNASSIGNED_ID || validContainerIds.has(groupId),
    ),
  )

  return { groups, assignments }
}

export const buildCustomFlatCalendars = (
  leaves: FlatCalendarNode[],
  config: CustomGroupsConfig,
): FlatCalendarNode[] => {
  const flat: FlatCalendarNode[] = []
  const groupsByParent = config.groups.reduce<Record<string, CustomGroup[]>>((acc, group) => {
    const parentKey = group.parentId ?? '__root__'
    acc[parentKey] ??= []
    acc[parentKey].push(group)
    return acc
  }, {})

  const appendGroups = (parentId: string | null) => {
    const parentKey = parentId ?? '__root__'
    for (const group of groupsByParent[parentKey] ?? []) {
      flat.push({ id: group.id, parentId: group.parentId, fullname: group.name, email: null, nodeType: 'group' })
      appendGroups(group.id)
    }
  }

  appendGroups(null)
  flat.push({ id: CUSTOM_UNASSIGNED_ID, parentId: null, fullname: 'Senza gruppo', email: null, nodeType: 'unassigned' })
  for (const leaf of leaves) {
    const gId = config.assignments[leaf.id]
    const valid = gId && (config.groups.some((g) => g.id === gId) || gId === CUSTOM_UNASSIGNED_ID)
    flat.push({ ...leaf, parentId: valid ? gId : CUSTOM_UNASSIGNED_ID, nodeType: 'leaf' })
  }
  return flat
}

export const flattenCalendarsGrouped = (
  grouped: { fullname: string; email: string | null; items: any[] }[],
): FlatCalendarNode[] => {
  const flat: FlatCalendarNode[] = []
  for (const rootGroup of grouped) {
    const rootId = `group_${rootGroup.fullname}`
    flat.push({ id: rootId, parentId: null, fullname: rootGroup.fullname, email: null })
    for (const item of rootGroup.items) {
      if (item.items) {
        const deptId = `dept_${item.fullname}`
        flat.push({ id: deptId, parentId: rootId, fullname: item.fullname, email: null })
        for (const leaf of item.items) {
          flat.push({
            id: leaf.email ?? `user_${leaf.userid}`,
            parentId: deptId,
            fullname: leaf.fullname,
            email: leaf.email,
            color: leaf.color,
          })
        }
      } else {
        flat.push({
          id: item.email ?? `room_${item.userid}`,
          parentId: rootId,
          fullname: item.fullname,
          email: item.email,
          color: item.color,
          status: item.status,
          isAvailable: item.isAvailable,
          isFakePerson: item.isFakePerson,
        })
      }
    }
  }
  return flat
}

export const hslToHex = (hsl: string): string => {
  const match = hsl.match(/hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/)
  if (!match) return hsl
  const h = parseInt(match[1]) / 360
  const s = parseInt(match[2]) / 100
  const l = parseInt(match[3]) / 100
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255)
  const g = Math.round(hue2rgb(p, q, h) * 255)
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255)
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

export const colorWithAlpha = (color: string | undefined, alpha: number): string => {
  if (!color) return `rgba(128,128,128,${alpha})`
  // hex → rgba
  const hex = color.match(/^#([0-9a-f]{6})$/i)
  if (hex) {
    const n = parseInt(hex[1], 16)
    return `rgba(${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff},${alpha})`
  }
  // hsl → hsla
  const hsl = color.match(/^hsl\((.+)\)$/)
  if (hsl) return `hsla(${hsl[1]},${alpha})`
  // già rgba/hsla o altro: restituisce invariato
  return color
}

export const getRecurrenceLabel = (recurrenceType?: string): string | null => {
  switch (recurrenceType) {
    case 'SeriesMaster':
      return 'Serie ricorrente'
    case 'Occurrence':
      return 'Ricorrente'
    case 'Exception':
      return 'Ricorrente (modificato)'
    default:
      return null
  }
}
